import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { isEmpty } from "lodash";
import { filterData } from "../../../../src/utils/kendo";
import { setNewMyTasks } from "../../../actions/task";
import { setInitialProjectChangedFromText, setInitialProjectId } from "../../../actions/taskSidebar";
import { kendo, number, route } from "../../../config";
import { icon, label, placeholder, quote } from "../../../config/constants";
import { getQueryParams } from "../../../helper/common";
import { useCombinationKeys } from "../../../helper/commonHooks";
import { compareLists } from "../../../utils/common";
import { useKanbanUpdates } from "../../KanbanView/hooks/kanban.hooks";
import { checkForWhitespace } from "../../OverallSearch/search.helper";
import { setName } from "../../Tasks/tasks.helper";
import { handleTaskKeyUpdate } from "../../Tasks/tasks.service";
import { checkIsPersonalProject, setSidebarFieldsDisabled } from "../sidebar.helper";

/**
 * HeaderForm component handles inputs of TaskSidebarHeader
 * @props {}
 */
const HeaderForm = (props) => {
  const location = useLocation();
  const [taskName, setTaskName] = useState("");
  const [project, setProject] = useState();
  const [userProjectsList, setUserProjectsList] = useState([]);
  const { isTaskNameEmpty, setIsTaskNameEmpty } = props;
  const { allProjectsList, privacyStatus } = useSelector((state) => state.tasks.defaultDetails);
  const { user } = useSelector((store) => store.auth);
  const { isNewTask, task, newTaskData } = useSelector((state) => state.taskSidebar);
  const loader = useSelector((state) => state.taskSidebar?.loaderComponent?.name);
  const { pathname } = useLocation();
  const { assignedId, initialProjectChangedFromText } = newTaskData;
  const [projectList, setProjectList] = useState([]);
  const ctrlS = useCombinationKeys("s");
  const { workflowBasicDetails, workflowFeatures } = useSelector((state) => state.taskWorkflowDetails);
  const { isCloneTask, cloneTask } = useSelector((state) => state.cloneTaskDetails);
  const { selectedView } = useSelector((state) => state.tasks);
  const { sharedWorkflowDetail } = useSelector((state) => state.taskSidebar);
  const { allTasks } = useSelector((state) => state.tasks);
  const dispatch = useDispatch();
  const sharedProjectId = getQueryParams("pId", location.search) || sharedWorkflowDetail.sharedProjectId;
  const hasAccess = useMemo(() => {
    return userProjectsList?.find((project) => project?.ProjectId === assignedId);
  }, [userProjectsList, assignedId]);

  const personalProject = useMemo(() => {
    return allProjectsList?.find((project) => project.IsPersonal && user.id === project.user);
  }, [allProjectsList, user.id]);

  const taskNameInput = useRef(null);
  const { updateKanbanName } = useKanbanUpdates();
  const taskSidebarPath = pathname === route.PRIVATE_ROUTE.TASK_SIDEBAR.PATH;
  const getInitialProjects = () => {
    const userProjectList = allProjectsList?.filter((project) => {
      return project.RoleId || (project.IsPersonal && project.user === user.id);
    });

    if (userProjectList?.length) {
      const personalBoard = userProjectList.find((project) => project.IsPersonal && project.user === user.id);
      const sortedProjects = userProjectList
        .filter((project) => !project.IsPersonal || project.user !== user.id)
        .sort((a, b) => {
          const labelA = (a.label || "").toLowerCase().replace(/^[^a-zA-Z0-9]+/, "");
          const labelB = (b.label || "").toLowerCase().replace(/^[^a-zA-Z0-9]+/, "");
          return labelA.localeCompare(labelB);
        });

      return personalBoard ? [personalBoard, ...sortedProjects] : sortedProjects;
    }
    return [];
  };

  /**
   * used to get all the projects, a user is part of
   * Filters and sorts user's project list, placing personal board * * first and alphabetically sorting the rest
   * @author {Prachi Jain}
   */
  useEffect(() => {
    const finalList = getInitialProjects();
    setUserProjectsList(finalList);
    setProjectList(finalList);
  }, [allProjectsList]);

  /**
   * sets focus to task name input
   */
  useEffect(() => {
    if (taskNameInput.current) {
      taskNameInput.current.focus();
    }
  }, [task.taskId, isNewTask]);

  /**
   * Ctrl+s shortcut to save
   */
  useEffect(() => {
    handleCtrlShortCut();
  }, [ctrlS]);

  /**
   * sets intital value of project where task is created.
   * @author Himanshu Negi
   */
  useEffect(() => {
    if (isNewTask && !isCloneTask && (!project || !compareLists(projectList, userProjectsList))) {
      if (
        matchPath(pathname, { path: route.PRIVATE_ROUTE.TASKS.ROUTER_PATH }) ||
        matchPath(pathname, { path: route.PRIVATE_ROUTE.ORGANISATION_DASHBOARD }) ||
        matchPath(pathname, { path: route.PRIVATE_ROUTE.TEAM_SETTING.ROUTER_PATH }) ||
        matchPath(pathname, { path: route.PRIVATE_ROUTE.EMAILS.ROUTER_PATH })
      ) {
        let paramsData = matchPath(pathname, { path: route.PRIVATE_ROUTE.TASKS.ROUTER_PATH });
        paramsData = paramsData ? paramsData : matchPath(pathname, { path: route.PRIVATE_ROUTE.TEAM_SETTING.ROUTER_PATH });
        paramsData = paramsData ? paramsData : matchPath(pathname, { path: route.PRIVATE_ROUTE.EMAILS.ROUTER_PATH });
        paramsData = paramsData ? paramsData : matchPath(pathname, { path: route.PRIVATE_ROUTE.ORGANISATION_DASHBOARD });

        const { params } = paramsData;
        const finalList = getInitialProjects();
        const finalListMap = new Map();
        let personalProject = null;
        finalList.forEach((p) => {
          finalListMap.set(parseInt(p.value), p);
          if (p.IsPersonal && p.ProjectCode === user?.projectCode) personalProject = p;
        });
        if (finalListMap.size === 0) return;
        let initialProject = finalListMap.get(parseInt(sharedProjectId));
        initialProject = initialProject ? initialProject : finalListMap.get(parseInt(params?.projectId));
        initialProject = initialProject ? initialProject : personalProject;
        setProject(initialProject);
      }
    }
  }, [userProjectsList, isNewTask, pathname, sharedProjectId]);

  /**
   * Initial project in case of clone task
   */
  useEffect(() => {
    if (isCloneTask && isNewTask) {
      const requiredProjectId = cloneTask?.flowElements?.findLast((f) => f.isCompleted)?.assignmentProjectId;
      let intitialProject = userProjectsList?.find((p) => p.value == (requiredProjectId ? requiredProjectId : parseInt(sharedProjectId)));
      intitialProject = intitialProject
        ? intitialProject
        : userProjectsList?.find((p) => p.IsPersonal === number.ONE && p?.ProjectCode == user?.projectCode);
      setProject(intitialProject);
    }
  }, [isCloneTask, isNewTask, userProjectsList]);

  /**
   * sets value of project in newTaskData global state
   */
  useEffect(() => {
    if (!initialProjectChangedFromText && project) {
      project && handleTaskKeyUpdate(isNewTask, "project", project?.value);
    }
  }, [project]);

  useEffect(() => {
    if (isEmpty(taskName)) setIsTaskNameEmpty(true);
    else setIsTaskNameEmpty(false);
  }, [taskName]);

  // used to prepolulate task name of selected workflow
  useEffect(() => {
    if (isNewTask && !isCloneTask && workflowBasicDetails?.TaskName && workflowFeatures[number.ONE]?.IsSelected === number.ONE) {
      setTaskName(workflowBasicDetails?.TaskName);
      handleTaskKeyUpdate(isNewTask, "name", workflowBasicDetails?.TaskName?.trim());
    }
  }, [isNewTask, isCloneTask, workflowBasicDetails?.TaskName, workflowFeatures]);

  useEffect(() => {
    if (isNewTask && isCloneTask && cloneTask?.name) {
      setTaskName(cloneTask?.name);
      handleTaskKeyUpdate(isNewTask, "name", cloneTask?.name?.trim());
    }
  }, [isCloneTask, isNewTask, cloneTask?.name]);

  useEffect(() => {
    if (task?.taskId && !isCloneTask) {
      setTaskName(task?.Name);
    } else if (task?.taskId && isCloneTask) {
      setTaskName(task?.Name);
    }
  }, [task?.taskId, isCloneTask]);

  /**
   * Sets name in newTaskData state
   * @param {void}
   * @returns {void}
   */
  const handleCtrlShortCut = async () => {
    if (isNewTask) {
      await handleTaskNameOnBlur();
    }
  };

  /**
   * handles project dropdown state change
   * @param {void}
   * @returns {void}
   */
  const handleProjectChange = (e) => {
    setProject(e.target.value);
    dispatch(setInitialProjectChangedFromText(false));
  };

  /**
   * changes task name on blur event
   * @param {void}
   * @returns {void}
   */
  const handleTaskNameOnBlur = async (e) => {
    if (!isEmpty(taskName) && !isNewTask) {
      const response = await handleTaskKeyUpdate(isNewTask, "name", taskName?.trim());
      if (selectedView === label.PIVOT) updateKanbanName(task?.taskId, taskName?.trim());
      if (response && !taskSidebarPath) {
        const payload = setName(allTasks, taskName?.trim(), task?.taskId);
        dispatch(setNewMyTasks(payload));
      }
    }
  };

  /**
   * filter out the user project search data.
   * @param {*} event
   */
  const filterChange = (event) => {
    let project = userProjectsList;
    setProjectList(filterData(event.filter, project));
  };

  /**
   * Handles change for Task name input
   * @param {Event} e
   * @author Himanshi Chawla
   */
  const handleChange = (e) => {
    const value = e.target.value;
    if (!checkForWhitespace(value)) return;
    setTaskName(value);
    if (isNewTask) handleTaskKeyUpdate(isNewTask, "name", value);
  };

  /**
   * custom dropdown item render for user project list
   * @returns JSX
   * @author Himanshu Negi
   *
   */
  const itemRender = (li, itemProps) => {
    const currentProject = allProjectsList?.find((project) => itemProps?.dataItem?.value === project?.ProjectId);
    const isPersonalProject = checkIsPersonalProject(currentProject?.ProjectId);
    const currentProjectPrivacy = allProjectsList?.find((project) => itemProps?.dataItem?.value === project?.ProjectId)?.Privacy;
    const currentProjectPrivacyIcon = privacyStatus?.find((p) => p.PrivacyId === currentProjectPrivacy)?.Icon;
    const itemChildren = (
      <span key={itemProps?.index} className='privacy-dropdown-values d-flex align-content-center' title={itemProps?.dataItem?.label}>
        <div className='privacy-dropdown-values-icon mr-2'>{isPersonalProject ? icon.USER_ICON : icon[currentProjectPrivacyIcon]}</div>
        <span className='text-truncate'>{itemProps?.dataItem?.label}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  /**
   * custom dropdown value render for user project list
   * @returns JSX
   * @author Himanshu Negi
   */
  const valueRender = (element, value) => {
    if (!value) {
      return element;
    }
    const currentProject = allProjectsList?.find((project) => value?.value === project?.ProjectId);
    const isPersonalProject = checkIsPersonalProject(currentProject?.ProjectId);
    const currentProjectPrivacy = allProjectsList?.find((project) => value?.value === project?.ProjectId)?.Privacy;
    const currentProjectPrivacyIcon = privacyStatus?.find((p) => p.PrivacyId === currentProjectPrivacy)?.Icon;
    const children = [
      <span key={value?.value} className='d-flex align-items-center justify-content-center'>
        <span>{isPersonalProject ? icon.USER_ICON : icon[currentProjectPrivacyIcon]}</span>
        <span>{value?.label}</span>
      </span>,
    ];
    return React.cloneElement(element, { ...element.props }, children);
  };

  /**
   * handles switching from personal teams
   * @author Sejal
   */
  const handlePersonalProjectChange = () => {
    dispatch(setInitialProjectChangedFromText(true));
    const selectedProject = userProjectsList?.find((project) => project.ProjectId == assignedId);
    if (selectedProject) {
      setProject(selectedProject);
      dispatch(setInitialProjectId(selectedProject?.ProjectId));
    }
  };

  /**
   * handles switching from  teams
   * @author Sejal
   */
  const handleProjectSwitch = () => {
    dispatch(setInitialProjectChangedFromText(true));
    setProject(personalProject);
    dispatch(setInitialProjectId(personalProject?.ProjectId));
  };
  return (
    <>
      {isNewTask && (
        <div className='new-task-project-select'>
          <div className='d-flex'>
            <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
              <DropDownList
                className='max-width-300 tour-task-sidebar-project'
                name='taskProject'
                data={projectList ? projectList : userProjectsList}
                textField='label'
                dataItemKey='value'
                id='dropdown-header-form-project-list'
                value={project}
                onChange={handleProjectChange}
                filterable={true}
                onFilterChange={filterChange}
                itemRender={itemRender}
                valueRender={valueRender}
              />
            </Tooltip>
            {!!(hasAccess && project?.IsPersonal) && (
              <div onClick={handlePersonalProjectChange} className='switch-teams p-1 align-items-center d-flex  cursor-pointer mb-0 ml-2'>
                <span className='d-flex align-items-center justify-content-center text-icon-warning'>{icon.SWITCH_WARNING}</span>
                <span>{quote.PERSONAL_INITIATATED}</span>
              </div>
            )}
            {!!(hasAccess && !project?.IsPersonal) && (
              <div onClick={handleProjectSwitch} className='switch-teams p-1 align-items-center d-flex  cursor-pointer mb-0 ml-2'>
                <span className='d-flex align-items-center justify-content-center text-icon-warning'>{icon.SWITCH_WARNING}</span>
                <span>{quote.TEAM_SWITCH}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <Input
        type='text'
        name='taskName'
        placeholder={placeholder.ENTER_TASK_NAME}
        autoComplete='off'
        id='tname'
        autoFocus
        ref={taskNameInput}
        value={taskName}
        onChange={handleChange}
        onBlur={handleTaskNameOnBlur}
        onFocus={() => setIsTaskNameEmpty(false)}
        className={`tour-task-sidebar-taskname ${isTaskNameEmpty ? "form-control-invalid" : ""}`}
        required={isTaskNameEmpty && true}
        maxLength={kendo.TASK_INPUT_LENGTH}
        disabled={loader || setSidebarFieldsDisabled()}
      />
      {loader && <div className='preloader loader-wrap'></div>}
    </>
  );
};

export default HeaderForm;
